<template>
  <div class="payout-methods">
    <div class="row justify-between items-center">
      <h2 v-translate>
        Payout Methods
      </h2>

      <Loader v-if="isLoadingLinks" />
      <button
        v-else-if="!isLoading && !isImpersonateMode && !isAllMethodsRegistered"
        class="secondary"
        @click="showAddNewDialog"
      >
        <Icon name="plus-circle" />
        <span v-translate>Add new</span>
      </button>
    </div>

    <Notification
      v-if="error"
      icon-name="danger"
      :title="$gettext('Failed retrieving your payout methods')"
      :message="error"
    />

    <Loader v-else-if="isLoading" />

    <div
      v-else-if="isImpersonateMode"
      class="panel-body"
    >
      <em v-translate>Please use the admin panel to manage payout methods for this user.</em>
    </div>

    <div
      v-else
      class="payout-accounts"
    >
      <div
        v-if="hasRejectedPayoneerAccount"
        class="text-body-light no-payment-method"
      >
        <p v-translate>
          Your Payoneer account application has been <strong>declined</strong>.
        </p>
        <p v-translate>
          Please <a
            href="https://support.dittomusic.com/"
            target="_blank"
          >contact our support team</a>.
        </p>
      </div>
      <p
        v-else-if="hasPendingPayoneerAccount"
        v-translate
        class="text-body-light no-payment-method"
      >
        We’re still processing your Payoneer account. Please check back later.
      </p>
      <p
        v-if="!payoutAccounts.length"
        v-translate
        class="text-body-light no-payment-method"
      >
        Set up a payout method to withdraw your royalties.
      </p>
      <template v-else>
        <div
          v-for="account in listedPayoutMethods"
          :key="`${account.type}-${account.id}`"
          class="payout-method-option read-only items-center"
          :class="account.status"
        >
          <div :class="`method-image ${account.type}`">
            <img
              :src="String(getPayoutMethodImg(account.type).imgSrc)"
              :alt="getPayoutMethodImg(account.type).imgAltText"
            >
            <div
              v-if="account.status === payoutAccountStatuses.active"
              v-translate
              class="tag"
            >
              Active
            </div>
            <div
              v-else-if="account.status === payoutAccountStatuses.pending"
              v-translate
              class="tag"
            >
              Pending
            </div>
            <div
              v-else-if="account.status === payoutAccountStatuses.blocked"
              v-translate
              class="tag blocked"
            >
              Blocked
            </div>
          </div>
          <div class="method-name">
            <h4>{{ account.name }}</h4>
            <p
              v-if="account.isVerified"
              class="method-status"
            >
              <Icon
                name="verified"
                fill="success"
              />
              <strong
                v-translate
                class="text-success"
              >Confirmed</strong>
            </p>
          </div>
          <div v-if="isAccountEditable(account)">
            <button
              class="edit-method tertiary button-small button-round"
              :disabled="isLoadingLinks"
              @click="openTipaltiDialog"
            >
              <Icon name="edit" />
            </button>
          </div>
        </div>
      </template>
    </div>

    <PayoutMethodOptionsDialog
      ref="AddNewDialog"
      @payoneerSelected="redirectToPayoneer"
      @tipaltiSelected="openTipaltiDialog"
    />
    <Dialog
      ref="TipaltiDialog"
      class="tipalti-dialog"
      dismissible
      size="large"
      :title="hasTipaltiAccount ? $gettext('Edit a Payout Method') : $gettext('Setup a Payout Method')"
      @dialogClosed="getPayoutAccounts"
    >
      <div
        v-if="widgetLink"
        class="dialog-body"
      >
        <iframe
          :src="widgetLink"
          :title="$gettext('Manage Payout Methods')"
        />
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Action, Getter, State } from 'vuex-class'
import Dialog from '@/components/CommonUI/Dialog.vue'
import Icon from '@/components/CommonUI/Icon.vue'
import Loader from '@/components/CommonUI/Loader.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import PayoutMethodOptionsDialog from '@/components/PayoutMethodOptionsDialog.vue'
import { AccountTypes, PayoutAccountStatuses } from '@/constants'
import { getErrorMessage } from '@/helpers/errorHandler'

@Options({
  components: {
    Dialog,
    Icon,
    Loader,
    Notification,
    PayoutMethodOptionsDialog,
  },
})

export default class PayoutMethods extends Vue {
  error: string | null = null
  isLoading = false
  isLoadingLinks = false
  registrationLink: string | null = null
  widgetLink: string | null = null

  $refs!: {
    AddNewDialog: InstanceType<typeof PayoutMethodOptionsDialog>,
    TipaltiDialog: InstanceType<typeof Dialog>,
  }

  @State payoutAccounts!: PayoutAccount[]

  @Getter activePayoutMethods!: PayoutAccount[]
  @Getter hasPendingPayoneerAccount!: boolean
  @Getter hasRejectedPayoneerAccount!: boolean
  @Getter isImpersonateMode!: boolean
  @Getter hasTipaltiAccount!: boolean

  get listedPayoutMethods() {
    return this.payoutAccounts
      .filter(
        (method, index, allMethods) => allMethods.findIndex(m => m.type === method.type) === index,
      )
      .sort((a, b) => (b.type !== AccountTypes.payoneer ? -1 : 1))
  }

  get isAllMethodsRegistered() {
    // 2 methods: Payoneer and Tipalti Bank Transfer/Paypal
    return this.listedPayoutMethods.length === 2
  }

  // eslint-disable-next-line class-methods-use-this
  get payoutAccountStatuses() {
    return PayoutAccountStatuses
  }

  @Action getUserPayoutAccounts!: () => Promise<void>
  @Action getPayoneerRegistrationLink!: () => Promise<string>
  @Action getTipaltiWidgetLink!: () => Promise<string>

  // eslint-disable-next-line class-methods-use-this
  isAccountEditable(account: PayoutAccount): boolean {
    return account.type !== AccountTypes.payoneer
  }

  getPayoutMethodImg(accountType: string) {
    switch (accountType) {
      case AccountTypes.tipaltiPaypal:
        return {
          imgSrc: new URL('../assets/paypal-logo.svg', import.meta.url),
          imgAltText: 'Paypal logo',
        }
      case AccountTypes.payoneer:
        return {
          imgSrc: new URL('../assets/payoneer-logo.svg', import.meta.url),
          imgAltText: 'Payoneer logo',
        }
      default:
        return {
          imgSrc: new URL('../assets/bank-building.svg', import.meta.url),
          imgAltText: this.$gettext('Bank building'),
        }
    }
  }

  async getPayoutAccounts(): Promise<void> {
    this.isLoading = true

    try {
      await this.getUserPayoutAccounts()
    } catch (error) {
      this.error = getErrorMessage(error)
    }

    this.isLoading = false
  }

  async redirectToPayoneer() {
    this.$refs.AddNewDialog.hide()
    this.isLoadingLinks = true

    try {
      this.registrationLink = await this.getPayoneerRegistrationLink()
    } catch (error) {
      this.error = getErrorMessage(error)
    }

    this.isLoadingLinks = false

    // setTimeout to prevent issue with redirect in async call being blocked by iOS Safari adblocker
    setTimeout(() => {
      if (this.registrationLink) window.open(this.registrationLink, '_blank')
    })
  }

  async showAddNewDialog() {
    if (!this.error) this.$refs.AddNewDialog.show()
  }

  async openTipaltiDialog() {
    this.$refs.AddNewDialog.hide()

    this.isLoadingLinks = true
    try {
      this.widgetLink = await this.getTipaltiWidgetLink()

      this.$refs.TipaltiDialog.show()
    } catch (error: unknown) {
      this.error = getErrorMessage(error)
    }
    this.isLoadingLinks = false
  }

  async created() {
    this.getPayoutAccounts()
  }
}
</script>

<style lang="scss">
.payout-methods {

  @media (--max-medium) {
    margin-top: 2rem;
  }

  h2 {
    margin: 0;
  }

  .dialog h3 {
    color: var(--brand-body-light);
    font-family: var(--brand-font-body);
    font-weight: 600;
    text-align: center;
  }

  iframe {
    border: 0;
    height: calc(90vh - 11rem); // large dialog max-height - padding and title height
    width: 100%;
  }

  .no-payment-method {
    border: 1px solid var(--medium-grey);
    border-radius: 10px;
    padding: 1.5rem;
    margin-bottom: 10px;
  }
}

.payout-method-option {
  border: 1px solid transparent;
  border-radius: 1rem;
  display: flex;
  margin: 0 0 10px;
  padding: 1rem;

  &.pending,
  &.active {
    border-color: var(--info);
  }

  &.blocked {
    border-color: var(--soft-red);
  }

  &:not(.read-only):hover,
  &:not(.read-only):focus {
    border-color: var(--info);
    cursor: pointer;
  }

  .tag {
    background-color: var(--info);
    border-radius: 14px;
    color: white;
    font-size: 12px;
    font-weight: 600;
    line-height: 2;
    text-transform: uppercase;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    padding: 0 6px;
    min-width: 70px;

    &.blocked {
      background-color: var(--soft-red);
    }
  }

  h4,
  p {
    margin: 0;
  }

  .method-image {
    background: #F5F5FF;
    border: 1px solid var(--darkening-grey);
    border-radius: 10px;
    margin-right: 1rem;
    padding: 10px;
    width: 90px;
    height: 70px;
    line-height: 45px;
    text-align: center;
    position: relative;

    img {
      vertical-align: middle;
    }

    &.tipalti-echeck img,
    &.tipalti-no-payment-method img,
    &.tipalti-ach img,
    &.tipalti-wire-transfer img {
      width: 50%;
    }
  }

  .method-name {
    flex-grow: 1;
  }

  .method-status {
    display: flex;
    align-items: center;

    .icon {
      margin-right: .5rem;
      width: 1rem;
      height: 1rem;
    }
  }
}
</style>
